import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, ButtonGroup, Button, InputGroup, Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Dropdown, Menu, MenuMenu } from 'semantic-ui-react';
import { validate as uuidValidate } from 'uuid';
import { Host } from '../../../../../models';
import { Loader, ModalComponent } from '../../../../../components';
import { resetHostActions, runHostAction, loadHost, loadTktInstances, loadTktInstancePihyveUsers } from '../../../../../redux';
import _ from 'lodash';
import moment from 'moment';

let HostActions = ({ hostId }) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const [nLogLines, setNLogLines]                       = useState(50);
    const [logSearch, setLogSearch]                       = useState('');
    const [reverseLogs, setReverseLogs]                   = useState(false);
    const [currentTktInstance, setCurrentTktInstance]     = useState(null);
    const [reconfigIsVisible, showReconfig]               = useState(false);
    const [reconfiguration, setReconfiguration]           = useState({ api_key: null, uri: null });
    const [reconfigurationError, setReconfigurationError] = useState(null);
    const [needsLogsRefresh, setNeedsLogsRefresh]         = useState(false);
    const [wifiConfError, setWifiConfError]               = useState(null);
    const [wifiConfigIsVisible, showWifiConfig]           = useState(false);
    const [wifiConf, setWifiConf]                         = useState({ ssid: '', password: '' });
    const [confirmIsVisible, showConfirm]                 = useState(false);
    const [confirmationContent, setConfirmationContent]   = useState(null);

    const { user } = useSelector(state => state.auth);
    const {
        tktInstances,
        pihyveUsers,
        isLoadingPihyveUsers
    } = useSelector(state => state.tktInstances);

    const {
        host, lastAction, isLoadingOne,
        lastActionResult, lastActionError,
        lastActionStdOut, lastActionStdErr
    } = useSelector(state => state.hosts);

    useEffect(() => {
        dispatch(resetHostActions());
    }, [dispatch]);

    useEffect(() => {
        if (user)
            setCurrentTktInstance(user.tkt_instance);
    }, [user]);

    useEffect(() => {
        if (needsLogsRefresh && host?.status === 'connected') {
            setNeedsLogsRefresh(false);
            dispatch(runHostAction(host._id, Host.ACTION_PIHYVE_LOGS, {n: nLogLines}));
        }
    }, [host?.status]);

    useEffect(() => {
        if (currentTktInstance) {
            setReconfiguration({
                ...reconfiguration,
                uri: currentTktInstance.engine_url.replace('http', 'ws')
            });
            dispatch(loadTktInstancePihyveUsers(currentTktInstance.id));
        }
    }, [currentTktInstance]);

    useEffect(() => {
        dispatch(loadTktInstances());
    }, []);

    useEffect(() => {
        if (user && tktInstances?.length)
            onInstanceChange(
                /*e*/{},
                /*data*/{ value: user.tkt_instance_id }
            );
    }, [ user, tktInstances ]);

    const refresh = React.useCallback(_.throttle((_id, logSearch, nLogLines) => {
        dispatch(runHostAction(_id, Host.ACTION_PIHYVE_LOGS, {
            n: nLogLines,
            search: logSearch
        }));
    }, 1000, {
        'leading': false,
        'trailing': true
    }), []);

    const onSearchChanged = React.useCallback(e => setLogSearch(e.target.value), []);
    const onNLogChanged = React.useCallback(e => setNLogLines(e.target.value), []);

    useEffect(() => {
        if (host?._id === hostId)
            refresh(host._id, logSearch, nLogLines);
    }, [host?._id, logSearch, nLogLines]);

    const runAction = (action, params, needsConfirm, needsRefreshLogs) => {
        if (!host || host._id !== hostId)
            return false;

        const run = () => {
            showConfirm(false);
            dispatch(runHostAction(host._id, action, params, (err, result) => {
                if (needsRefreshLogs > 0)
                    setNeedsLogsRefresh(true);
            }));
        };

        if (!needsConfirm)
            return run();

        setConfirmationContent((
            <Row>
                <Col>
                    <hr />
                    <h6 className="text-bold">
                        <i className="fa fa-terminal" />&nbsp;&nbsp;
                        { t(`hosts.action_${action}`) }
                    </h6>
                    <hr />
                    <ButtonGroup className="btn-block">
                        <Button color="warning" onClick={() => showConfirm(false) }>
                            <i className="fa fa-times" />&nbsp;
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" onClick={() => run() }>
                            <i className="fa fa-bolt" />&nbsp;
                            {t('common.yes')}
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
        ));
        showConfirm(true);
    };

    const instanceOptions = tktInstances
        ?.filter(instance => instance.is_active)
        ?.map(instance => {
            return {
                key: instance.id,
                text: instance.name,
                value: instance.id
            };
        });

    const userOptions = pihyveUsers
        ?.filter(user => !!user.apikey['X-API-Key'])
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map(user => {
            return {
                key: user.apikey['X-API-Key'],
                text: user.name,
                description: moment(user.apikey.valid_until).format('L'),
                value: user.apikey['X-API-Key']
            };
        });

    const onInstanceChange = (e, data) => {
        const instance = tktInstances?.find(i => i.id === data.value);
        setCurrentTktInstance(instance);
    }

    const onApiKeyChange = (e, data) => {
        setReconfiguration({
            ...reconfiguration,
            api_key: data.value
        });
    }

    const checkPihyveConfiguration = async () => {
        setReconfigurationError(null);

        if (!reconfiguration.uri?.length)
            return setReconfigurationError(t('hosts.uri_is_invalid'));

        if (!uuidValidate(reconfiguration.api_key))
            return setReconfigurationError(t('hosts.api_key_is_invalid'));

        return runAction(Host.ACTION_PIHYVE_RECONFIGURE, reconfiguration, /*needsConfirm*/true);
    }

    const onWifiSsidChanged = (e) => {
        setWifiConf({
            ...wifiConf,
            ssid: e.target.value
        });
    }

    const onWifiPasswordChanged = (e) => {
        setWifiConf({
            ...wifiConf,
            password: e.target.value
        });
    }

    const checkWifiConfiguration = async () => {
        setWifiConfError(null);

        if (!wifiConf.ssid?.length)
            return setWifiConfError(t('hosts.wifi_ssid_is_invalid'));
        if (!wifiConf.password?.length)
            return setWifiConfError(t('hosts.wifi_password_is_invalid'));

        return runAction(Host.ACTION_SET_CUSTOM_NETWORK, wifiConf, /*needsConfirm*/true);
    }

    if (!host && !lastAction)
        return <Loader />;

    const actionButton = (action, params, icon, color = 'info', needsConfirm, needsRefreshLogs) => (
        <Button color={ color } disabled={isLoadingOne || host?.status !== 'connected'} onClick={() => runAction(action, params, needsConfirm, needsRefreshLogs)}>
            <span>
                <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : icon}`}></i> &nbsp;
                {t(`hosts.action_${action}`, params)}
            </span>
        </Button>
    );

    const toConsoleContent = content => {
        if (_.isObject(content))
            content = JSON.stringify(content, null, 4);

        if (lastAction !== Host.ACTION_PIHYVE_LOGS)
            return content;

        content = content
            ?.split('\n')
            ?.map((row, index) => (
                <div key={index}>
                    <span className="line-number">{index + 1}</span>&nbsp;
                    <span>{row}</span>
                </div>
            ));

        if (reverseLogs)
            content = content?.reverse();

        return content;
    }

    return (
        <>
            {confirmIsVisible && (
                <ModalComponent title={t('hosts.are_you_sure_to_run_this_action')}>
                    { confirmationContent }
                </ModalComponent>
            )}
            <Row>
                <Col>
                    <Row>
                        <Col className="text-left">
                            <h6 className="text-bold">
                                <i className="fa fa-power-off" />&nbsp;
                                {t('hosts.shutdown_or_reboot')}
                            </h6>
                            <ButtonGroup className="btn-block">
                                { actionButton(Host.ACTION_REBOOT, {}, 'repeat', 'warning', /*needsConfirm*/true) }
                                { actionButton(Host.ACTION_SHUTDOWN, {}, 'power-off', 'danger', /*needsConfirm*/true) }
                            </ButtonGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-left">
                            <h6 className="text-bold">
                                <i className="fa fa-print" />&nbsp;
                                {t('hosts.print_management')}
                            </h6>
                            <ButtonGroup className="btn-block">
                                { actionButton(Host.ACTION_SHOW_PRINT_QUEUE, {}, 'print') }
                                { actionButton(Host.ACTION_CANCEL_PRINT_QUEUE, {}, 'ban', 'warning') }
                            </ButtonGroup>
                        </Col>
                    </Row>
                    {user?.isSuperAdmin() && (
                        <Row>
                            <Col className="text-left">
                                <h6 className="text-bold">
                                    <i className="fa fa-bar-chart" />&nbsp;
                                    {t('hosts.system_status')}
                                </h6>
                                <ButtonGroup className="btn-block">
                                    { actionButton(Host.ACTION_PROCESSES, {}, 'bar-chart') }
                                    { actionButton(Host.ACTION_DISK_USAGE, {}, 'pie-chart') }
                                    { actionButton(Host.ACTION_LS_USB, {}, 'usb') }
                                </ButtonGroup>
                            </Col>
                        </Row>
                    )}
                    {user?.isSuperAdmin() && (
                        <Row>
                            <Col className="text-left">
                                <h6 className="text-bold">
                                    <i className="fa fa-sitemap" />&nbsp;
                                    {t('hosts.network_connections')}
                                </h6>
                                <ButtonGroup className="btn-block">
                                    { actionButton(Host.ACTION_LIST_NETWORKS, {}, 'sitemap') }
                                    { actionButton(Host.ACTION_SHOW_IPS, {}, 'sitemap') }
                                    { actionButton(Host.ACTION_LIST_WIFI_SSIDS, {}, 'wifi') }
                                </ButtonGroup>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col>
                    <Row>
                        <Col className="text-left">
                            <h6 className="text-bold">
                                <i className="fa fa-object-group" />&nbsp;
                                {t('hosts.pihyve_service')}
                            </h6>
                            <ButtonGroup className="btn-block">
                                { actionButton(Host.ACTION_RESTART_PIHYVE, {}, 'repeat', 'warning', /*needsConfirm*/true, /*needsRefreshLogs*/true) }
                                { user?.isSuperAdmin() && actionButton(Host.ACTION_CHECK_UPGRADE, {}, 'cloud-download', 'danger') }
                            </ButtonGroup>
                        </Col>
                    </Row>
                    {user?.isSuperAdmin() && (
                        <Row>
                            <Col className="text-left">
                                <h6 className="text-bold">
                                    <i className="fa fa-github" />&nbsp;
                                    {t('hosts.git_repository')}
                                </h6>
                                <ButtonGroup className="btn-block">
                                    { actionButton(Host.ACTION_GIT_PULL, {}, 'github', 'warning') }
                                    { actionButton(Host.ACTION_GIT_STATUS, {}, 'github') }
                                    { actionButton(Host.ACTION_GIT_DIFF, {}, 'github') }
                                    { actionButton(Host.ACTION_INSTALL_DEPS, {}, 'github', 'warning') }
                                </ButtonGroup>
                            </Col>
                        </Row>
                    )}
                    { user?.isSuperAdmin() && !reconfigIsVisible && (
                    <Row>
                        <Col>
                            <h6 className="text-bold">
                                <i className="fa fa-gear" />&nbsp;
                                {t('hosts.pihyve_configuration')}
                            </h6>
                            <ButtonGroup className="btn-block">
                                { actionButton(Host.ACTION_PIHYVE_CONFIG, {}, 'cog') }
                                <Button color="danger" disabled={isLoadingOne || host?.status !== 'connected'} onClick={() => showReconfig(true)}>
                                    <span>
                                        <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'wrench'}`}></i> &nbsp;
                                        {t(`hosts.action_${Host.ACTION_PIHYVE_RECONFIGURE}`)}
                                    </span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                    )}
                    { user?.isSuperAdmin() && reconfigIsVisible && (
                        <>
                            <Row>
                                <Col>
                                    <h6 className="text-bold">
                                        <i className="fa fa-gear" />&nbsp;
                                        {t('hosts.print_management')}
                                    </h6>
                                    <Menu>
                                        <Dropdown
                                            options={instanceOptions}
                                            onChange={onInstanceChange}
                                            defaultValue={user?.tkt_instance_id}
                                            loading={isLoadingOne}
                                            search
                                            selection
                                            className="dropdown-custom-width"
                                        />
                                        <MenuMenu position='right'>
                                            <Dropdown
                                                options={userOptions}
                                                onChange={onApiKeyChange}
                                                loading={isLoadingPihyveUsers}
                                                search
                                                selection
                                                clearable
                                                className="w-100"
                                                placeholder={t('hosts.api_key_placeholder')}
                                            />
                                        </MenuMenu>
                                    </Menu>
                                    {reconfigurationError && (
                                        <div className="alert alert-danger">
                                            { reconfigurationError }
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col className="text-left">
                                    <ButtonGroup className="btn-block">
                                        <Button color="dark" disabled={isLoadingOne} onClick={() => showReconfig(false)}>
                                            <span>
                                                <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'times'}`}></i> &nbsp;
                                                {t('common.cancel')}
                                            </span>
                                        </Button>
                                        <Button color="danger" disabled={isLoadingOne || !(reconfiguration.api_key?.length === 36)} onClick={checkPihyveConfiguration}>
                                            <span>
                                                <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'wrench'}`}></i> &nbsp;
                                                {t(`hosts.action_${Host.ACTION_PIHYVE_RECONFIGURE}`)}
                                            </span>
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                    {user?.isSuperAdmin() && (
                        <Row>
                            <Col className="text-left">
                                <h6 className="text-bold">
                                    <i className="fa fa-wifi" />&nbsp;
                                    {t('hosts.custom_network_connection')}
                                </h6>
                                <ButtonGroup className="btn-block">
                                    { actionButton(Host.ACTION_SHOW_CUSTOM_NETWORK, {}, 'eye') }
                                    { actionButton(Host.ACTION_TEST_CUSTOM_NETWORK, wifiConf, 'sitemap', 'warning', /*needsConfirm*/true) }
                                </ButtonGroup>
                            </Col>
                        </Row>
                    )}
                    {user?.isSuperAdmin() && !wifiConfigIsVisible && (
                        <Row>
                            <Col className="text-left">
                                <ButtonGroup className="btn-block">
                                    <Button color="warning" disabled={isLoadingOne || host?.status !== 'connected'} onClick={() => showWifiConfig(true)}>
                                        <span>
                                            <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'wrench'}`}></i> &nbsp;
                                            {t(`hosts.action_${Host.ACTION_SET_CUSTOM_NETWORK}`)}
                                        </span>
                                    </Button>
                                    { actionButton(Host.ACTION_DELETE_CUSTOM_NETWORK, {}, 'trash', 'danger', /*needsConfirm*/true) }
                                </ButtonGroup>
                            </Col>
                        </Row>
                    )}
                    { user?.isSuperAdmin() && wifiConfigIsVisible && (
                        <>
                            <Row>
                                <Col>
                                    <h6 className="text-bold">
                                        <i className="fa fa-gear" />&nbsp;
                                        {t('hosts.network_connections')}
                                    </h6>
                                    <InputGroup className="w-100">
                                        <Input type="text" value={ wifiConf.ssid } onChange={ onWifiSsidChanged } placeholder={t('hosts.wifi_ssid')} />
                                        <Input type="text" value={ wifiConf.password } onChange={ onWifiPasswordChanged } placeholder={t('hosts.wifi_password')}/>
                                    </InputGroup>
                                    {wifiConfError && (
                                        <div className="alert alert-danger">
                                            { wifiConfError }
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col className="text-left">
                                    <ButtonGroup className="btn-block">
                                        <Button color="dark" disabled={isLoadingOne} onClick={() => showWifiConfig(false)}>
                                            <span>
                                                <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'times'}`}></i> &nbsp;
                                                {t('common.cancel')}
                                            </span>
                                        </Button>
                                        <Button color="warning" disabled={isLoadingOne} onClick={checkWifiConfiguration}>
                                            <span>
                                                <i className={`fa fa-${isLoadingOne ? 'spinner fa-spin' : 'wrench'}`}></i> &nbsp;
                                                {t(`hosts.action_${Host.ACTION_SET_CUSTOM_NETWORK}`)}
                                            </span>
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                </Col>
            </Row>
            <Row>
                <Col className="text-center">
                    <div className="consoles-wrapper">
                        <InputGroup className="w-100">
                            <Input type="text" className="max-w-14" value={ logSearch } onChange={ onSearchChanged } placeholder={t('hosts.search_in_logs')} />
                            <Input type="number" className="max-w-6" min={ 10 } max={ 1000 } value={ nLogLines } onChange={ onNLogChanged } />
                            <ButtonGroup className="w-100-w-20">
                                <Button className="w-4" color="dark" onClick={() => setReverseLogs(!reverseLogs)}>
                                    <i className={`fa fa-arrow-${reverseLogs ? 'up' : 'down'}`} />
                                </Button>
                                { actionButton(Host.ACTION_PIHYVE_LOGS, {n: nLogLines, search: logSearch}, 'file-text-o') }
                            </ButtonGroup>
                        </InputGroup>
                        {lastActionStdOut && (
                            <pre className="console text-white text-left p-3">
                                { toConsoleContent(lastActionStdOut) }
                            </pre>
                        )}
                        {lastActionStdErr && (
                            <pre className="console bg-warning text-left p-3 text-white">
                                { toConsoleContent(lastActionStdErr) }
                            </pre>
                        )}
                        {lastActionError && (
                            <pre className="console bg-danger text-left p-3 text-white">
                                { toConsoleContent(lastActionError) }
                            </pre>
                        )}
                    </div>
                </Col>
            </Row>
        </>
    );
}

export default HostActions;
